import { Fragment, useEffect, useRef } from "react"

import { Flex, Form, Steps } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { PlayCircleOutlined } from "@/icons"
import { DocumentationLink } from "@/molecules/DocumentationLink"
import { HiddenFields } from "@/organisms/Form/components/ConditionsSectionForm/components/ConditionList/HiddenFields"
import {
  cweIDValidator,
  testIsValidID,
  vulnerabilityIDValidator
} from "../../helpers"
import { InputConditionSection } from "../InputConditionSection"

import styles from "./ConditionSection.module.scss"

import type { ConditionSectionComponent } from "."

export const ConditionSection: ConditionSectionComponent = (props) => {
  const { form, conditionsOptions, rules, setFormIsDirty, disabled } = props
  const conditionsFieldName = "currentConditions"
  const conditions = form.getFieldValue(conditionsFieldName)
  const cviRules = [
    { message: "Invalid value", validator: vulnerabilityIDValidator },
    ...rules
  ]
  const cweRules = [
    { message: "Invalid value", validator: cweIDValidator },
    ...rules
  ]
  const rulesMap = {
    cveIds: cviRules,
    cweIds: cweRules
  }
  const focusLabelRef = useRef<number>()

  // eslint-disable-next-line react-hooks/purity
  if (!focusLabelRef.current) focusLabelRef.current = Date.now()

  const { t: tConditionSection } = useTranslation("translation", {
    keyPrefix: "settings.policies.form.conditions"
  })

  Form.useWatch(conditionsFieldName, form)

  useEffect(() => {
    document.getElementById(`label-${focusLabelRef.current}`)?.focus()
    focusLabelRef.current = Date.now()
    document
      .querySelectorAll('[data-input-condition-key="cveIds"]')
      .forEach((el) => {
        const items = el.querySelectorAll(".ant-select-selection-item")

        items.forEach((item) => {
          const value = item.getAttribute("title")

          if (value && !testIsValidID(value)) {
            item.classList.add("error-tag-input")
          }
        })
      })
  }, [conditions])

  const validateForm = () => {
    form.validateFields()
    setFormIsDirty()
  }

  return (
    <Form.List name={conditionsFieldName}>
      {(steps, stepsActions) => {
        return (
          <Steps
            className={styles.container}
            rootClassName="rcn"
            orientation="vertical"
            size="small"
            current={conditions.length - 2}
            items={steps.map((step, stepIndex) => {
              const selectedValues = conditions?.[stepIndex] || []
              const selectedIds = selectedValues.reduce(
                (
                  acc: Record<string, boolean>,
                  { value }: { value: string }
                ) => {
                  acc[value] = true
                  return acc
                },
                {}
              )

              const currentConditionsOptions = conditionsOptions.map(
                ({
                  options,
                  ...rest
                }: {
                  options: { id: string; opts: [] }[]
                }) => ({
                  options: options.map((option) => {
                    if (selectedIds[option.id]) {
                      selectedIds[option.id] = option

                      return { disabled: true, ...option }
                    }

                    return option
                  }),
                  ...rest
                })
              )
              const fieldNamePrefix = [conditionsFieldName, stepIndex]

              return {
                key: step.key,
                title:
                  stepIndex === 0
                    ? tConditionSection("when")
                    : tConditionSection("or"),
                icon: <PlayCircleOutlined className={styles.icon} />,
                description: (
                  <div className={styles.step}>
                    <Form.List
                      name={step.name}
                      rules={[
                        {
                          message: tConditionSection("rules.more"),
                          validator: async (_, values) => {
                            if (!values?.length) {
                              const isRequired =
                                props.required && stepIndex === 0

                              if (isRequired) return Promise.reject()

                              return Promise.resolve()
                            }

                            const allOptions = conditionsOptions.flatMap(
                              ({ options }: { options: unknown[] }) => options
                            )

                            const allCannotBeUsedAlone = values.every(
                              (value: { value: string }) => {
                                const option = allOptions.find(
                                  ({ id }: { id: string }) => id === value.value
                                )
                                return option?.canBeUsedAlone === false
                              }
                            )

                            if (allCannotBeUsedAlone) {
                              return Promise.reject()
                            }
                            return Promise.resolve()
                          }
                        }
                      ]}
                    >
                      {(fields, actions, { errors }) => {
                        return (
                          <>
                            {fields.map((condition, index) => {
                              if (!selectedValues[index])
                                return (
                                  <HiddenFields
                                    key={condition.key}
                                    name={condition.name}
                                  />
                                )

                              const {
                                value,
                                key = value,
                                selectedValue,
                                focusId
                              } = selectedValues[index]
                              const {
                                valueType,
                                opts,
                                displayName,
                                description,
                                placeholder,
                                defaultValue,
                                links
                              } = selectedIds[value]
                              const currentRules =
                                rulesMap[key as keyof typeof rulesMap] || rules
                              const isLinkExist = !!links?.length
                              const Wrapper = isLinkExist ? Flex : Fragment
                              const wrapperProps = isLinkExist
                                ? { vertical: true, gap: "5px" }
                                : {}

                              return (
                                <Wrapper key={key} {...wrapperProps}>
                                  <HiddenFields name={condition.name} />
                                  <InputConditionSection
                                    id={focusId}
                                    label={displayName}
                                    description={description}
                                    disabled={disabled}
                                    rules={currentRules}
                                    key={condition.key}
                                    placeholder={placeholder}
                                    name={[index, "selectedValue"]}
                                    type={valueType}
                                    data-input-condition-key={key}
                                    options={opts}
                                    fieldNames={{ label: "displayName" }}
                                    onChange={validateForm}
                                    onReady={() => {
                                      if (defaultValue && !selectedValue) {
                                        const fieldName = [
                                          ...fieldNamePrefix,
                                          index,
                                          "selectedValue"
                                        ]
                                        form.setFieldValue(
                                          fieldName,
                                          defaultValue
                                        )
                                      }
                                    }}
                                    onRemove={() => {
                                      if (
                                        fields.length === 1 &&
                                        steps.length > 2
                                      )
                                        stepsActions.remove(stepIndex)
                                      else actions.remove(index)
                                      setFormIsDirty()
                                    }}
                                  />
                                  {isLinkExist && (
                                    <DocumentationLink href={links[0].url}>
                                      {links[0].displayText}
                                    </DocumentationLink>
                                  )}
                                </Wrapper>
                              )
                            })}
                            <Form.ErrorList errors={errors} />
                            <InputConditionSection
                              labelInValue
                              showSearch
                              rules={rules}
                              disabled={disabled}
                              fieldNames={{
                                label: "displayName",
                                value: "id"
                              }}
                              key={fields.length}
                              options={currentConditionsOptions}
                              onChange={(value) => {
                                actions.add(
                                  typeof value === "string"
                                    ? value
                                    : {
                                        focusId: `label-${focusLabelRef.current}`,
                                        ...value
                                      }
                                )

                                validateForm()

                                if (stepIndex === conditions.length - 1)
                                  stepsActions.add()
                              }}
                            />
                          </>
                        )
                      }}
                    </Form.List>
                  </div>
                )
              }
            })}
          />
        )
      }}
    </Form.List>
  )
}
